import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/IconButton';
import { ZONE_LABEL, ZONE_BG_CLASS } from './zoneStyles';
import { zoneAccentStyle } from './zoneAccent';
import {
    ZONE_ORDER,
    ZONE_WIDTH,
    WORLD_WIDTH,
    ARROW_STEP_PX,
    clampScroll,
    maxScroll,
    scrollForZone,
} from '../../utils/surfaceScene';

export interface ZoneStripProps {
    /** Current scroll offset of the `PlanetSurface` stage. */
    scrollX: number;
    /** Measured stage width. 0 before the first `ResizeObserver` measurement. */
    viewportWidth: number;
    /** Zone under the viewport centre, from `zoneIndexAt`. */
    activeZoneIndex: 0 | 1 | 2;
    /** Requests a scroll to the given (unclamped) world X; the caller clamps and eases. */
    onScrollTo: (target: number) => void;
    /** The fixed bottom dock's measured height. Defaults to 0 when no dock is mounted alongside the stage. */
    dockHeight?: number;
}

const STRIP_GAP_ABOVE_DOCK_PX = 16;
const SEGMENT_OPACITY = 0.32;
const VIEW_WINDOW_STYLE = {
    background: 'rgba(255,255,255,.18)',
} as const;

const HINT_TEXT = 'Drag the surface to explore · click the strip to jump';

/**
 * The strip renders inside the stage, whose root starts a world drag on
 * pointerdown. Without this, grabbing the minimap — or even a slightly sloppy
 * click on it — drags the scenery underneath instead of operating the control.
 */
function stopStagePointerDrag(event: ReactPointerEvent<HTMLDivElement>) {
    event.stopPropagation();
}

/**
 * Minimap "compass" strip (DS-16): shows the three zones, the visible slice
 * of the world, and jump controls. All position math is `surfaceScene.ts`
 * (DS-13) — this component only reads `scrollX`/`viewportWidth` and asks its
 * parent to scroll via `onScrollTo`, which is `PlanetSurface`'s own
 * `scrollTo` (already handling clamping, easing, and reduced motion).
 *
 * The strip is a real `role="slider"` for keyboard/AT users — dragging the
 * stage is not the only way to get around.
 */
export function ZoneStrip({ scrollX, viewportWidth, activeZoneIndex, onScrollTo, dockHeight = 0 }: ZoneStripProps) {
    const stripRef = useRef<HTMLDivElement | null>(null);
    const activeZone = ZONE_ORDER[activeZoneIndex];
    const [announcement, setAnnouncement] = useState(ZONE_LABEL[activeZone]);

    useEffect(() => {
        setAnnouncement(ZONE_LABEL[activeZone]);
    }, [activeZone]);

    const isFirstZone = activeZoneIndex === 0;
    const isLastZone = activeZoneIndex === ZONE_ORDER.length - 1;

    const goToPreviousZone = () => onScrollTo(scrollForZone(activeZoneIndex - 1, viewportWidth));
    const goToNextZone = () => onScrollTo(scrollForZone(activeZoneIndex + 1, viewportWidth));

    const jumpToFraction = (clientX: number) => {
        const rect = stripRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return;
        const fraction = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
        onScrollTo(clampScroll(fraction * WORLD_WIDTH - viewportWidth / 2, viewportWidth));
    };

    const handleStripClick = (event: MouseEvent<HTMLDivElement>) => {
        jumpToFraction(event.clientX);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                onScrollTo(clampScroll(scrollX - ARROW_STEP_PX, viewportWidth));
                break;
            case 'ArrowRight':
                event.preventDefault();
                onScrollTo(clampScroll(scrollX + ARROW_STEP_PX, viewportWidth));
                break;
            case 'PageUp':
                event.preventDefault();
                onScrollTo(clampScroll(scrollX - ZONE_WIDTH, viewportWidth));
                break;
            case 'PageDown':
                event.preventDefault();
                onScrollTo(clampScroll(scrollX + ZONE_WIDTH, viewportWidth));
                break;
            case 'Home':
                event.preventDefault();
                onScrollTo(scrollForZone(0, viewportWidth));
                break;
            case 'End':
                event.preventDefault();
                onScrollTo(scrollForZone(ZONE_ORDER.length - 1, viewportWidth));
                break;
            default:
                break;
        }
    };

    const viewWindowLeftPct = (clampScroll(scrollX, viewportWidth) / WORLD_WIDTH) * 100;
    const viewWindowWidthPct = (viewportWidth / WORLD_WIDTH) * 100;

    return (
        <div
            data-testid="zone-strip"
            data-tutorial-id="zone-links"
            onPointerDown={stopStagePointerDrag}
            className="absolute left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1.5"
            style={{ bottom: dockHeight + STRIP_GAP_ABOVE_DOCK_PX }}
        >
            <div className="flex items-center gap-1.5 text-value" style={zoneAccentStyle(activeZone)}>
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[color:var(--zone-accent)]" />
                <span data-testid="zone-strip-label" className="text-[color:var(--zone-accent)]">
                    {ZONE_LABEL[activeZone]}
                </span>
            </div>

            <div className="flex items-center gap-2">
                <IconButton
                    aria-label="Previous zone"
                    size="md"
                    disabled={isFirstZone}
                    onClick={goToPreviousZone}
                >
                    <Icon name="chevron-left" size={16} />
                </IconButton>

                <div
                    ref={stripRef}
                    role="slider"
                    tabIndex={0}
                    aria-label="Planet surface position"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(maxScroll(viewportWidth))}
                    aria-valuenow={Math.round(clampScroll(scrollX, viewportWidth))}
                    aria-valuetext={ZONE_LABEL[activeZone]}
                    onClick={handleStripClick}
                    onKeyDown={handleKeyDown}
                    className="relative w-[240px] sm:w-[340px] h-[22px] rounded-full overflow-hidden border border-hairline-strong flex outline-none focus-visible:ring-2 focus-visible:ring-hairline-strong cursor-pointer"
                >
                    {ZONE_ORDER.map((zone) => (
                        <div
                            key={zone}
                            className={cn('flex-1', ZONE_BG_CLASS[zone])}
                            style={{ opacity: SEGMENT_OPACITY }}
                        />
                    ))}
                    <div
                        data-testid="zone-strip-view-window"
                        className="absolute inset-y-0 border-[1.5px] border-content-primary pointer-events-none"
                        style={{ ...VIEW_WINDOW_STYLE, left: `${viewWindowLeftPct}%`, width: `${viewWindowWidthPct}%` }}
                    />
                </div>

                <IconButton aria-label="Next zone" size="md" disabled={isLastZone} onClick={goToNextZone}>
                    <Icon name="chevron-right" size={16} />
                </IconButton>
            </div>

            <p className="hidden sm:block text-micro text-content-muted">{HINT_TEXT}</p>

            <div data-testid="zone-strip-live-region" aria-live="polite" className="sr-only">
                {announcement}
            </div>
        </div>
    );
}

export default ZoneStrip;
